import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { DatesIntervalRequest, DatesInterval } from '../../global/interfaces';
import * as valuations from '../valuations/valuations.actions';
import { map, switchMap, catchError, tap } from 'rxjs/operators';
import { concat, EMPTY, from, Observable, of } from 'rxjs';
import { HttpService } from '../../global/http.service';
import {
  ValuationsPortfolio,
  ValuationsPrices,
  ValuationsPortfolioRequest,
  ValuationsPricesRequest,
  ValuationsCsvRequestPayload,
  CSV_FILENAME
} from './valuations.models';
import { downloadFile } from '../../global/fileDownload';

import { ToastService } from '../../shared/toaster_/toaster_.service';
import { Actions as ActionsCDK } from 'atlas-cdk';

@Injectable()
export class ValuationsEffects {
  public loadValuationsDates$ = createEffect(() => this.actions$.pipe(
    ofType<valuations.LoadValuationsDatesAction>(valuations.ActionTypes.LOAD_VALUATIONS_DATES),
    map(action => action.payload),
    switchMap((payload: DatesIntervalRequest) =>
      this.loadValuationsDates(payload).pipe(
        map((response: DatesInterval) =>
          new valuations.LoadValuationsDatesSuccessAction(response)
        ),
        catchError(error =>
          //   from([
          //   new valuations.LoadValuationsDatesFailedAction(error),
          //   new AddErrorToastAction(error.error.message)
          // ])
          // {
          //   this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
          //   return of(new valuations.LoadValuationsDatesFailedAction(error));
          // }
          concat(
            of(new valuations.LoadValuationsDatesFailedAction(error)),
            of(null).pipe(
              tap(() => {
                this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
              })
            )
          )
        )
      )
    )
  ));

  public loadValuationsPortfolio$ = createEffect(() => this.actions$.pipe(
    ofType<valuations.LoadValuationsPortfolioAction>(valuations.ActionTypes.LOAD_VALUATIONS_PORTFOLIO),
    switchMap(action =>
      this.loadValuationsPortfolio(action.payload).pipe(
        map((response: ValuationsPortfolio) =>
          new valuations.LoadValuationsPortfolioSuccessAction(response)
        ),
        catchError(error => from([
          new valuations.LoadValuationsPortfolioFailedAction(error)
        ]))
      )
    )
  ));

  public loadValuationsPrices$ = createEffect(() => this.actions$.pipe(
    ofType<valuations.LoadValuationsPricesAction>(valuations.ActionTypes.LOAD_VALUATIONS_PRICES),
    switchMap(action =>
      this.loadValuationsPrices(action.payload).pipe(
        map((response: ValuationsPrices[]) =>
          new valuations.LoadValuationsPricesSuccessAction(response)
        ),
        catchError(error =>
          //    from([
          //   new valuations.LoadValuationsPricesFailedAction(error),
          //   new AddErrorToastAction(error.error.message)
          // ])
          // this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
          // return of(new valuations.LoadValuationsPricesFailedAction(error));
          concat(
            of(new valuations.LoadValuationsPricesFailedAction(error)),
            of(null).pipe(
              tap(() => {
                this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
              })
            )
          )
        )
      )
    )
  ));

  public loadValuationsTotalPrices$ = createEffect(() => this.actions$.pipe(
    ofType<valuations.LoadValuationsTotalPricesAction>(valuations.ActionTypes.LOAD_VALUATIONS_TOTAL_PRICES),
    switchMap(action =>
      this.loadValuationsTotalPrices(action.payload).pipe(
        map((response: ValuationsPrices[]) =>
          new valuations.LoadValuationsTotalPricesSuccessAction(response)
        ),
        catchError(error =>
          //    {
          //   this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
          //   return of(new valuations.LoadValuationsTotalPricesFailedAction(error));
          // }
          //   from([
          //   new valuations.LoadValuationsTotalPricesFailedAction(error),
          //   new AddErrorToastAction(error.error.message)
          // ])
          concat(
            of(new valuations.LoadValuationsTotalPricesFailedAction(error)),
            of(null).pipe(
              tap(() => {
                this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
              })
            )
          )
        )
      )
    )
  ));

  public exportValuationsCsv$ = createEffect(() => this.actions$.pipe(
    ofType<valuations.ValuationsCsvExportAction>(valuations.ActionTypes.EXPORT_VALUATIONS_CSV),
    map(action => action.payload),
    switchMap(payload =>
    ((payload.exportType === 'regular' ? this.exportValuationsCsv(payload) : this.exportValuationsHistoryCsv(payload)).pipe(
      map((response: any) => {
        // deepcode ignore DOMXSS:internally created secure response
        downloadFile(response.body, 'text/csv', CSV_FILENAME[payload.exportType]);
      }),
      catchError(() => {
        this._toasterService.launchAlertError("Download failed. Please try again later.", ActionsCDK.error);
        return EMPTY;
      }

      )
    )
    ))
  ), { dispatch: false });

  public constructor(
    private readonly actions$: Actions,
    private readonly http: HttpService,
    private readonly _toasterService: ToastService
  ) {
  }

  private loadValuationsDates(payload: DatesIntervalRequest): Observable<DatesInterval> {
    return this.http.get(`/metrics/${payload.account}/date-ranges/valuations`);
  }

  private loadValuationsPortfolio(payload: ValuationsPortfolioRequest): Observable<ValuationsPortfolio> {
    return this.http.post(`/valuations/portfolio/${payload.account}`, { from: payload.from, to: payload.to });
  }

  private loadValuationsPrices(payload: ValuationsPricesRequest): Observable<ValuationsPrices[]> {
    return this.http.post(`/valuations/prices/${payload.account}`, { funds: payload.funds, from: payload.from, to: payload.to });
  }

  /**
   * Same endpoint as loadValuationsPrices, instead of dispatching list of funds it's an empty string
   */
  private loadValuationsTotalPrices(payload: ValuationsPricesRequest): Observable<ValuationsPrices[]> {
    return this.http.post(`/valuations/prices/${payload.account}`, { funds: payload.funds, from: payload.from, to: payload.to });
  }

  private exportValuationsCsv(payload: ValuationsCsvRequestPayload): Observable<any> {
    payload.type = 'csv';
    return this.http.post(`/valuations/portfolio/${payload.account}/export`, payload, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  private exportValuationsHistoryCsv(payload: ValuationsCsvRequestPayload): Observable<any> {
    return this.http.post(`/valuations/portfolio/${payload.account}/history-export`, payload, {
      responseType: 'blob',
      observe: 'response',
    });
  }
}
